// --------------------------------------------------------
// ATTENDANCE RECORD - AttendanceRecord
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------

import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Header,
  HeaderTable,
  SearchContainer,
  SolidButton,
  Table,
  TBody,
  Title,
  TR,
} from "./AttendanceRecord.styled";
import Colors from "../../../../constants/Colors";
import { H1, H2 } from "../../../../components/UI/generalLayout/Layout.styled";
import { CircularProgress, OutlinedInput } from "@mui/material";
import { Search } from "@mui/icons-material";
import CareLineDropdown from "../../../../components/dropdown/CareLineDropdown";
import * as appointmentActions from "../../../../store/actions/appointment";
import * as attendanceRecordActions from "../../../../store/actions/attendanceRecord";
import { getYearBeforeToday } from "../../../../utils/date.utils";
import { Spacer } from "../../../../components/UI/spacer/spacer";
import ErrorArea from "../../../../components/error/ErrorArea";
import { Text } from "../../../../components/UI/generalLayout/Layout.styled";
import { useLocation } from "react-router-dom";
import AttendanceCard from "../../../../components/AttendanceCard/AttendanceCard";
import useResponsiveInfo from "../../../../hooks/useResponsiveInfo";
import OpenAppointments from "./components/openAppointments/OpenAppointments";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../..";
import { Paths } from "../../../../enums/Paths";
import { AppointmentStatus } from "../../../../enums/AppointmentStatus";
import { SchedulingService } from "../../../../core/http/service/SchedulingService";
import ModalAttendance from "./components/modalAttendanceRecord/ModalAttendanceRecord";
import * as careLineActions from "../../../../store/actions/careLine";
// --------------------------------------------------------
// ATTENDANCE RECORD INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */

interface IAttendanceRecord {
  pessoa: any;
  agendamento: any;
}

interface YearLabel {
  nome: string;
  id: string;
}

export interface IAttendance {
  id: string;
  horario: string;
  tipoProfissional: string;
  nomeProfissional: string;
}
export interface IDataOpen {
  id: string;
  date: string;
  status: string;
}

const allValue = { nome: "Todos", id: "" };

const years = getYearBeforeToday(5);

const yearsLabels = years.map<YearLabel>(value => ({
  nome: value,
  id: value,
}));

const yearsFilter = [...yearsLabels, { nome: "Todos", id: "" }];

function AttendanceRecord({
  pessoa,
  agendamento,
}: IAttendanceRecord): JSX.Element {
  // --------------------------------------------------------
  // ATTENDANCE RECORD PRIVATE DECLARATIONS
  // --------------------------------------------------------

  const { state } = useLocation();
  const { isMobile } = useResponsiveInfo();
  const { id } = (state as any) ?? {};

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [modalAttendance, setModalAttendance] = useState(false);
  const [idAtendimento, setIdAtendimento] = useState("");
  const [valueSearch, setValueSearch] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<YearLabel>(allValue);
  const [isAgendamentoRealizado, setIsAgendamentoRealizado] =
    useState<boolean>(true);
  const [error, setError] = useState<any>();

  const [hideAtendimentoButton, setHidenAtendimentoButton] =
    useState<boolean>(false);
  const [hasContentToShow, setHasContentToShow] = useState<boolean>(false);
  const [hasAgendamentoEmAberto, setHasAgendamentoEmAberto] =
    useState<boolean>(false);
  const [loading] = useState<boolean>(false);
  const { currentProfile }: any = useSelector((state: any) => state.login);

  const fetchAtendimentos = useCallback(async () => {
    await dispatch(
      appointmentActions.fetchAppointmentsByPacienteId(
        pessoa && pessoa.idPessoa ? pessoa.idPessoa : pessoa.id,
        selectedYear.nome
      )
    );
  }, [pessoa, selectedYear]);

  const { atendimentosEmAberto, atendimentos } = useSelector(
    (state: any) => state.appointment
  );

  const getExamData = useCallback(async () => {
    await dispatch(careLineActions.fetchDropdownData());
    await dispatch(careLineActions.fetchCareLines());
  }, []);

  useEffect(() => {
    // Função de limpeza assíncrona
    const cleanUp = async () => {
      console.log('Componente desmontado');
      await dispatch(appointmentActions.limparGerarImpressao());
    };

    // Retorna a função de limpeza
    return () => {
      cleanUp().catch(error => {
        console.error('Erro na função de limpeza:', error);
      });
    };
  }, [dispatch]);


  const attendance = useSelector((state: any) => state.attendance);

  const contentToShow =
    atendimentosEmAberto !== undefined &&
    atendimentosEmAberto !== null &&
    atendimentos !== undefined &&
    atendimentos !== null;

  const agendamentoEmAberto =
    atendimentosEmAberto !== undefined &&
    atendimentosEmAberto !== null &&
    atendimentosEmAberto.length > 0;

  const hideAtendimento = agendamentoEmAberto || isAgendamentoRealizado;

  const fetchIsAtendimentoRealizado = useCallback(async () => {
    const schedulingService: SchedulingService<any> = new SchedulingService();
    if (attendance.agendamento.id !== undefined) {
      const result = await schedulingService.getSimplifiedSchedulingById(
        agendamento.id
      );
      const isRealizado =
        result.data.data.statusPresenca !== null &&
        result.data.data.statusPresenca.toUpperCase() ===
        AppointmentStatus.V.toUpperCase();
      setIsAgendamentoRealizado(isRealizado);
    }
  }, [agendamento, attendance]);

  const changeAgendamento = useCallback(async () => {
    if (agendamento !== undefined) {
      await dispatch(attendanceRecordActions.changeAgendamento(agendamento));
    }
  }, [agendamento]);

  useEffect(() => {
    changeAgendamento();
    getExamData();
    fetchIsAtendimentoRealizado();
  }, [agendamento]);

  useEffect(() => {
    fetchIsAtendimentoRealizado();
  }, [attendance]);

  const deleteAtendimento = useCallback(
    async (id: string) => {
      await dispatch(appointmentActions.deleteAppointment(id));
      await fetchAtendimentos();
    },
    [id]
  );

  useEffect(() => {
    fetchAtendimentos();
  }, [selectedYear]);

  const conditionFilter = (item: any) =>
    valueSearch === ""
      ? true
      : item.nomeProfissional.toLowerCase().match(valueSearch.toLowerCase());

  return (
    <>
      {contentToShow && (
        <Container isMobile={isMobile}>
          <Header>
            <H1>Registro de Atendimentos</H1>
            {!hideAtendimento && (
              <SolidButton
                width={isMobile ? 90 : 30}
                style={{ height: 25 }}
                color={Colors.SPM600}
                onClick={() =>
                  navigate(Paths.RegistroAtendimento, {
                    state: {
                      pessoa: pessoa,
                      agendamento: attendance.agendamento,
                    },
                  })
                }
              >
                Registrar Atendimento
              </SolidButton>
            )}
          </Header>
          <Spacer top={30} />
          <SearchContainer>
            <OutlinedInput
              style={{
                borderRadius: "10px",
                width: isMobile ? "100%" : "450px",
              }}
              id="outlined-adornment-amount"
              endAdornment={<Search />}
              label=""
              placeholder="Buscar nome do profissional"
              value={valueSearch}
              onChange={(event: any) => {
                setValueSearch(event.target.value);
              }}
            />

            <CareLineDropdown
              value={selectedYear}
              onChange={setSelectedYear}
              items={yearsFilter}
              disabled={false}
              backgroundColor={Colors.SPM50}
              textColor={Colors.SPM600}
              placeholder="Selecione o ano"
              containerStyle={{
                width: isMobile ? "100%" : "25%",
              }}
              allValue={allValue}
            />
          </SearchContainer>
          <Spacer top={30} />
          <ErrorArea
            errorList={error}
            onCloseAction={() => {
              setError([]);
            }}
          />

          {atendimentosEmAberto?.length > 0 && (
            <>
              <H2 bold>Em aberto:</H2>
              <OpenAppointments
                data={atendimentosEmAberto}
                pessoa={pessoa}
                agendamento={attendance.agendamento}
                onDelete={deleteAtendimento}
              />
            </>
          )}
          {atendimentos?.filter(conditionFilter).length === 0 ? (
            <Text>Nenhum resultado encontrado</Text>
          ) : (
            <Table id="table" isMobile={isMobile}>
              <HeaderTable>
                <TR>
                  <Title bold>Data</Title>
                  <Title bold>Tipo do profissional</Title>
                  <Title bold>Nome do profissional</Title>
                  <Title></Title>
                  <Title></Title>
                </TR>
              </HeaderTable>
              <Spacer bottom={10} />
              {loading ? (
                <CircularProgress />
              ) : atendimentos?.length > 0 && id ? (
                atendimentos
                  ?.filter((d: any) => d.id === id)
                  .filter(conditionFilter)
                  .map((data: any) => {
                    return (
                      <TBody>
                        <AttendanceCard
                          data={data}
                          key={data.id}
                          pessoa={pessoa}
                          agendamento={attendance.agendamento}
                          openModal={() => {
                            setIdAtendimento(data.id);
                            setModalAttendance(true);
                          }}
                        />
                      </TBody>
                    );
                  })
              ) : (
                atendimentos?.filter(conditionFilter).map((data: any) => {
                  return (
                    <TBody>
                      <AttendanceCard
                        data={data}
                        key={data.id}
                        pessoa={pessoa}
                        agendamento={attendance.agendamento}
                        openModal={() => {
                          setIdAtendimento(data.id);
                          setModalAttendance(true);
                        }}
                      />
                    </TBody>
                  );
                })
              )}
            </Table>
          )}

          <ModalAttendance
            idAtendimento={idAtendimento}
            modalOpen={modalAttendance}
            onCloseModal={() => setModalAttendance(false)}
          />
        </Container>
      )}
    </>
  );
}

export default AttendanceRecord;
